import React, { useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Coin-redeem input rendered inside the cart / checkout summary.
 *
 * Reads the tenant's loyalty rules + the user's wallet on mount.
 * Emits an `onChange(coins)` callback whenever the user changes the
 * requested amount — the parent (Cart / Checkout page) is responsible
 * for actually POSTing `coins_redeemed` on `/api/orders/checkout`.
 *
 * Auto-clamps to the tenant's min/max caps + wallet balance.
 * Purely optional — if loyalty is disabled on this tenant, or the
 * user has < min-per-order coins, this component renders nothing so
 * it never adds clutter to non-loyalty tenants.
 */
export default function CoinsRedeemInput({ orderSubtotal = 0, value = 0, onChange }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/loyalty/me");
        if (data.enabled && data.balance >= data.min_coins_per_order) setState(data);
      } catch { /* hide */ }
    })();
  }, [user?.id]);

  if (!state) return null;

  const label = state.labels?.coin_plural || "Coins";
  const ratio = state.coin_to_inr_ratio || 1;
  const pctCap = Math.floor((orderSubtotal * (state.max_coins_per_order_pct / 100)) / ratio);
  const maxAllowed = Math.max(
    state.min_coins_per_order,
    Math.min(state.balance, state.max_coins_per_order, pctCap)
  );
  const clamped = Math.max(0, Math.min(value || 0, maxAllowed));
  const savings = Math.round(clamped * ratio);

  return (
    <div
      className="border border-amber-200 bg-amber-50/50 rounded-md p-3 my-3"
      data-testid="coins-redeem-input"
    >
      <div className="flex items-center justify-between text-sm">
        <div>
          <div className="font-medium">
            Use my {label}
            <span className="ml-2 text-xs text-neutral-500">
              (balance: {state.balance})
            </span>
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {state.min_coins_per_order}–{maxAllowed} redeemable · 1 {state.labels?.coin_singular || "coin"} = ₹{ratio}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-neutral-500">Discount</div>
          <div className="font-medium text-emerald-700" data-testid="coins-redeem-savings">
            −{money(savings)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <input
          type="number"
          min={0}
          max={maxAllowed}
          step={1}
          className="kb-input flex-1"
          value={clamped}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10) || 0;
            onChange?.(Math.max(0, Math.min(v, maxAllowed)));
          }}
          data-testid="coins-redeem-amount"
        />
        <button
          type="button"
          onClick={() => onChange?.(maxAllowed)}
          className="text-xs px-3 py-2 rounded-full border border-amber-300 hover:bg-amber-100"
          data-testid="coins-redeem-max"
        >
          Use max
        </button>
      </div>
    </div>
  );
}
